import * as React from 'react';
import { useEffect } from 'react';

import { useIntl } from 'react-intl';
import { useMatch } from 'react-router';

import { useEffectOnceOnCondition } from 'hooks/use-effect-once-on-condition';
import { useThrottle } from 'hooks/use-throttle';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { selectors, useAppSelector } from 'state/global-state';
import { useFlag } from 'state/launchdarkly';
import { RBIBrand, brand } from 'utils/environment';
import { EventName, emitEvent } from 'utils/event-hub';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import {
  PerformancePages,
  initializePagePerformance,
  pageLoaded,
  pageUnloaded,
  performanceReport,
} from 'utils/render-performance';

import { OffersRewards } from '../loyalty-offers-rewards/loyalty-offers-rewards';

import { OfferViewV1 } from './offer-view-v1';
import { OfferViewV2 } from './offer-view-v2';

const LoyaltyOffersBase = () => {
  const { formatMessage } = useIntl();

  // Track page loading performance
  initializePagePerformance(PerformancePages.OFFERS_PAGE);

  const { params } =
    useMatch({
      path: `${formatMessage({ id: 'routes.loyaltyHome' })}/:tab/*`,
    }) || {};

  const loyaltyEngineId = params?.['*'];
  const cdp = useCdpContext();

  const currentBrand = brand();
  const showRewardCarousel = useFlag(LaunchDarklyFlag.ENABLE_REWARD_CAROUSEL_IN_OFFERS_PAGE);
  const showOffersGridView = useFlag(LaunchDarklyFlag.ENABLE_OFFERS_GRID_VIEW_WITH_FILTER);

  const throttlePerformanceEvent = useThrottle(() => {
    cdp.trackEvent({
      name: CustomEventNames.LOYALTY_LOAD,
      type: EventTypes.Other,
      attributes: performanceReport(PerformancePages.OFFERS_PAGE),
    });
  }, 4000);

  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);

  useEffect(() => {
    initializePagePerformance(PerformancePages.OFFERS_PAGE);

    // If offers are still loading do nothing
    if (offersLoading) {
      return;
    }

    requestAnimationFrame(() => {
      // Page content loaded event
      pageLoaded(PerformancePages.OFFERS_PAGE);

      throttlePerformanceEvent();
    });

    return () => {
      pageUnloaded(PerformancePages.OFFERS_PAGE);
    };
  }, [offersLoading]);

  useEffectOnceOnCondition(
    () => {
      emitEvent(EventName.SCREEN_RENDER, {
        screenId: 'loyaltyOffers',
      });
    },
    () => !loyaltyEngineId
  );

  return (
    <>
      {showRewardCarousel && currentBrand === RBIBrand.BK && <OffersRewards />}
      {showOffersGridView ? <OfferViewV2 /> : <OfferViewV1 />}
    </>
  );
};

export const LoyaltyOffers = LoyaltyOffersBase;
